import Box from '@risk-smart/themed-cloudscape-components/box';
import Modal from '@risk-smart/themed-cloudscape-components/modal';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import type { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type ConfirmModalProps = {
  isVisible: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
  header: ReactNode;
  children: ReactNode;
  /** Overrides the translated default confirm-button label. */
  confirmLabel?: ReactNode;
  /**
   * When true, the confirm button shows a spinner and is disabled, and the
   * dismiss button is disabled. Modal dismissal (overlay/Esc) is also
   * suppressed so an in-flight async action cannot be cancelled mid-call,
   * which would let the user trigger the action twice.
   */
  loading?: boolean;
};

const ConfirmModal: FC<ConfirmModalProps> = ({
  isVisible,
  onConfirm,
  onDismiss,
  header,
  children,
  confirmLabel,
  loading = false,
}) => {
  const { t } = useTranslation(['common']);

  return (
    <Modal
      onDismiss={() => {
        if (!loading) {
          onDismiss();
        }
      }}
      visible={isVisible}
      closeAriaLabel={t('closeModal')}
      size={'small'}
      footer={
        <Box float={'left'}>
          <SpaceBetween direction={'horizontal'} size={'xs'}>
            <Button
              variant={'primary'}
              onClick={onConfirm}
              loading={loading}
              disabled={loading}
            >
              {confirmLabel ?? t('confirm')}
            </Button>
            <Button variant={'normal'} onClick={onDismiss} disabled={loading}>
              {t('cancel')}
            </Button>
          </SpaceBetween>
        </Box>
      }
      header={header}
    >
      {children}
    </Modal>
  );
};

export default ConfirmModal;
